// src/redux/services/store/storeAccountApi.ts
import type { EndpointBuilder } from "@reduxjs/toolkit/query";
import { baseApi } from "../baseApi";

export type StoreCustomerProfile = {
  id: number;
  user_id: number;
  first_name?: string | null;
  last_name?: string | null;
  full_name?: string | null;
  gender?: string | null;
  dob?: string | null;
  avatar_url?: string | null;
};

export type StoreCustomerProfileUpdate = {
  first_name?: string | null;
  last_name?: string | null;
  full_name?: string | null;
  gender?: string | null;
  dob?: string | null;
  avatar_url?: string | null;
};

export type StoreAddressType = "shipping" | "billing" | "other";

export type StoreAddress = {
  id: number;
  user_id: number;
  address_type: StoreAddressType;
  label?: string | null;
  contact_name: string;
  mobile_e164?: string | null;
  line1: string;
  line2?: string | null;
  landmark?: string | null;
  city: string;
  state: string;
  postal_code: string;
  country_code: string;
  latitude?: string | null;
  longitude?: string | null;
  is_default_billing: boolean;
  is_default_shipping: boolean;
  is_active: boolean;
};

export type StoreAddressCreate = {
  address_type?: StoreAddressType;
  label?: string | null;
  contact_name: string;
  mobile_e164?: string | null;
  line1: string;
  line2?: string | null;
  landmark?: string | null;
  city: string;
  state: string;
  postal_code: string;
  country_code?: string;
  latitude?: string | null;
  longitude?: string | null;
  make_default_billing?: boolean;
  make_default_shipping?: boolean;
};

export type StoreAddressUpdate = {
  address_type?: StoreAddressType;
  label?: string | null;
  contact_name?: string | null;
  mobile_e164?: string | null;
  line1?: string;
  line2?: string | null;
  landmark?: string | null;
  city?: string;
  state?: string;
  postal_code?: string;
  country_code?: string;
  latitude?: string | null;
  longitude?: string | null;
  is_active?: boolean;
  make_default_billing?: boolean;
  make_default_shipping?: boolean;
};

export type StoreSetDefaultAddressRequest = {
  address_id: number;
  kind: "shipping" | "billing";
};

export type MessageResponse = {
  message?: string;
  ok?: boolean;
};

type Builder = EndpointBuilder<any, any, any>;

export const storeAccountApi = baseApi.injectEndpoints({
  endpoints: (builder: Builder) => ({
    getStoreMyProfile: builder.query<StoreCustomerProfile, void>({
      query: () => ({
        url: `/store/me/profile`,
        method: "GET",
      }),
      providesTags: ["StoreProfile"],
    }),

    updateStoreMyProfile: builder.mutation<StoreCustomerProfile, StoreCustomerProfileUpdate>({
      query: (body) => ({
        url: `/store/me/profile`,
        method: "PUT",
        body,
      }),
      invalidatesTags: ["StoreProfile"],
    }),

    getStoreMyAddresses: builder.query<StoreAddress[], void>({
      query: () => ({
        url: `/store/me/addresses`,
        method: "GET",
      }),
      providesTags: ["StoreAddress"],
    }),

    createStoreAddress: builder.mutation<StoreAddress, StoreAddressCreate>({
      query: (body) => ({
        url: `/store/me/addresses`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["StoreAddress"],
    }),

    updateStoreAddress: builder.mutation<
      StoreAddress,
      { address_id: number; body: StoreAddressUpdate }
    >({
      query: ({ address_id, body }) => ({
        url: `/store/me/addresses/${address_id}`,
        method: "PATCH",
        body,
      }),
      invalidatesTags: ["StoreAddress"],
    }),

    deleteStoreAddress: builder.mutation<MessageResponse, number>({
      query: (address_id) => ({
        url: `/store/me/addresses/${address_id}`,
        method: "DELETE",
      }),
      invalidatesTags: ["StoreAddress"],
    }),

    setDefaultStoreAddress: builder.mutation<MessageResponse, StoreSetDefaultAddressRequest>({
      query: ({ address_id, kind }) => ({
        url: `/store/me/addresses/${address_id}/set-default`,
        method: "POST",
        params: { kind },
      }),
      invalidatesTags: ["StoreAddress"],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetStoreMyProfileQuery,
  useUpdateStoreMyProfileMutation,
  useGetStoreMyAddressesQuery,
  useCreateStoreAddressMutation,
  useUpdateStoreAddressMutation,
  useDeleteStoreAddressMutation,
  useSetDefaultStoreAddressMutation,
} = storeAccountApi;